import { supabase } from '@/lib/supabase';
import { withTiming } from '@/features/channels/utils/monitoring';
import { logger } from '@/features/channels/utils/logging';
import { ValidationError, BusinessError } from '@/features/channels/utils/errors';
import type { Channel, CreateChannelInput, UpdateChannelInput, UpdateChannelData } from '@/features/channels/types';
import { changeTrackingService } from '../changeTracking';
import { getTrackableFields } from '../changeTracking/fieldDefinitions';

/**
 * Core channel operations
 * @see src/services/channels/mutations.ts for mutation operations
 * @see src/services/channels/queries.ts for query operations
 * @see src/services/import/importService.ts for import related operations
 */
export const channelService = {
  getChannels: async (providerId: number, versionId: number) => {
    return withTiming('getChannels', async () => {
      try {
        // First get provider to check sequence setting
        const { data: _provider, error: providerError } = await supabase
          .from('providers')
          .select('has_sequence_numbers')
          .eq('id', providerId)
          .single();

        if (providerError) {
          logger.error(providerError, { providerId, versionId });
          throw new BusinessError('Failed to fetch provider settings');
        }

        // Get channels with ordered numbers
        const { data, error } = await supabase
          .from('channels')
          .select(`
            *,
            numbers:channel_numbers(
              id,
              type_id,
              number,
              is_sequence,
              type:channel_types(
                id,
                name,
                display_order
              )
            ),
            category:categories(
              id,
              name
            ),
            categories:channel_categories(
              id,
              category_id,
              is_default,
              category:categories(
                id,
                name,
                parent_id,
                display_order,
                price,
                description
              )
            ),
            plans:channel_plans(
              plan:plans(
                id,
                name,
                color_index
              )
            ),
            tags:channel_tag_assignments(
              tag_id,
              tag:channel_tags(
                id,
                name
              )
            ),
            linked_provider:providers!linked_provider_id(
              id,
              name,
              logo_url,
              type_id
            )
          `)
          .eq('provider_id', providerId)
          .eq('version_id', versionId)
          .order('name');

        if (error) {
          logger.error(error, { providerId, versionId });
          throw new BusinessError('Failed to fetch channels');
        }

        // Enhanced debug logging
        if (data && data.length > 0) {
          const channelSample = data[0];
          console.log('[ChannelService] Debug channel categories:', {
            channelId: channelSample.id,
            name: channelSample.name,
            hasCategories: Boolean(channelSample.categories),
            categories: channelSample.categories,
            categoryCount: channelSample.categories?.length || 0,
            is_local: channelSample.is_local
          });
        }

        return data as unknown as Channel[];
      } catch (error) {
        logger.error(error, { providerId, versionId });
        throw error;
      }
    });
  },

  createChannel: async (data: CreateChannelInput): Promise<Channel> => {
    return withTiming('createChannel', async () => {
      try {
        if (!data.providerId || !data.versionId) {
          throw new ValidationError('Provider ID and Version ID are required');
        }

        // Get provider's sequence setting
        const { data: provider, error: providerError } = await supabase
          .from('providers')
          .select('has_sequence_numbers')
          .eq('id', data.providerId)
          .single();

        if (providerError) {
          logger.error(providerError, { data });
          throw new BusinessError('Failed to fetch provider settings');
        }

        // Use centralized validation service
        const { channelValidationService } = await import('./validation');
        
        const validationContext = {
          providerId: data.providerId,
          versionId: data.versionId,
          is_local: data.is_local,
          is_duplicate: data.is_duplicate
        };

        const channelData = {
          name: data.name,
          numbers: data.numbers || []
        };

        const validation = await channelValidationService.validateChannel(channelData, validationContext);
        
        if (!validation.isValid) {
          throw new BusinessError(validation.errors[0] || 'Channel validation failed');
        }

        // Create the channel
        const { data: channel, error } = await supabase
          .from('channels')
          .insert({
            name: data.name,
            description: data.description,
            provider_id: data.providerId,
            version_id: data.versionId,
            category_id: data.categoryId,
            price: data.price,
            is_local: data.is_local || false,
            is_duplicate: data.is_duplicate || false
          })
          .select()
          .single();

        if (error) {
          logger.error(error, { data });
          throw new BusinessError('Failed to create channel', error);
        }

        // Handle multiple categories if provided
        if (data.categoryIds?.length) {
          const channelCategories = data.categoryIds.map(categoryId => ({
            channel_id: channel.id,
            category_id: categoryId,
            is_default: data.defaultCategoryId === categoryId ||
                       (data.defaultCategoryId === null && categoryId === data.categoryIds![0])
          }));

          const { error: categoriesError } = await supabase
            .from('channel_categories')
            .insert(channelCategories);

          if (categoriesError) {
            logger.error(categoriesError, { channel_id: channel.id, categoryIds: data.categoryIds });
            // Delete the channel if category creation fails
            await supabase.from('channels').delete().eq('id', channel.id);
            throw new BusinessError('Failed to create channel category associations');
          }

          // Track category assignments for new channel
          try {
            // Get version data for region_id
            const { data: version } = await supabase
              .from('provider_versions')
              .select('region_id')
              .eq('id', data.versionId)
              .single();

            if (version) {
              // Get category details for tracking
              const { data: categoryDetails } = await supabase
                .from('categories')
                .select('id, name')
                .in('id', data.categoryIds);

              if (categoryDetails) {
                for (const categoryData of categoryDetails) {
                  await changeTrackingService.logCategoryAssignment({
                    versionId: data.versionId,
                    providerId: data.providerId,
                    regionId: version.region_id,
                    channelId: channel.id,
                    actionType: 'CREATE',
                    newCategory: {
                      id: categoryData.id,
                      name: categoryData.name
                    }
                  });
                }
              }
            }
          } catch (trackingError) {
            logger.error('Failed to track category assignments:', { error: trackingError });
          }
        } 
        // Handle single category for backward compatibility
        else if (data.categoryId !== null && data.categoryId !== undefined) {
          const { error: categoryError } = await supabase
            .from('channel_categories')
            .insert({
              channel_id: channel.id,
              category_id: data.categoryId,
              is_default: true
            });

          if (categoryError) {
            logger.error(categoryError, { channel_id: channel.id, categoryId: data.categoryId });
            // Delete the channel if category creation fails
            await supabase.from('channels').delete().eq('id', channel.id);
            throw new BusinessError('Failed to create channel category association');
          }

          // Track single category assignment
          try {
            const [categoryResult, versionResult] = await Promise.all([
              supabase
                .from('categories')
                .select('name')
                .eq('id', data.categoryId)
                .single(),
              supabase
                .from('provider_versions')
                .select('region_id')
                .eq('id', data.versionId)
                .single()
            ]);

            if (categoryResult.data && versionResult.data) {
              await changeTrackingService.logCategoryAssignment({
                versionId: data.versionId,
                providerId: data.providerId,
                regionId: versionResult.data.region_id,
                channelId: channel.id,
                actionType: 'CREATE',
                newCategory: { id: data.categoryId, name: categoryResult.data.name }
              });
            }
          } catch (trackingError) {
            logger.error('Failed to track single category assignment:', { error: trackingError });
          }
        }

        const numberRecords = [];

        // Handle sequence numbers for sequential providers
        if (provider?.has_sequence_numbers) {
          const { error: numberError } = await supabase
            .rpc('assign_channel_number', {
              p_channel_id: channel.id,
              p_version_id: data.versionId
            });

          if (numberError) {
            logger.error(numberError);
            throw new BusinessError('Failed to assign sequence number');
          }
          
          // Fix any duplicate sequence numbers that may have been created
          await channelService.fixSequentialNumbers(data.providerId, data.versionId);
        } else {
          // Handle non-sequence numbers
          if (data.numbers?.length) {
            numberRecords.push(
              ...data.numbers
                .filter(n => !n.is_sequence)
                .map(n => ({
                  channel_id: channel.id,
                  type_id: n.typeId,
                  number: !n.number || n.number.toString().trim() === '' ? '*' : n.number.toString(),
                  is_sequence: false
                }))
            );

            // Create non-sequence numbers
            if (numberRecords.length > 0) {
              const { error: numbersError } = await supabase
                .from('channel_numbers')
                .insert(numberRecords);

              if (numbersError) {
                logger.error(numbersError, { numberRecords });
                await supabase.from('channels').delete().eq('id', channel.id);
                throw new BusinessError('Failed to create channel numbers');
              }
            }
          }
        }

        // Create plan associations
        if (data.planIds?.length) {
          const { error: plansError } = await supabase
            .from('channel_plans')
            .insert(
              data.planIds.map(planId => ({
                channel_id: channel.id,
                plan_id: planId,
                version_id: data.versionId
              }))
            );

          if (plansError) {
            logger.error(plansError, { channel_id: channel.id, planIds: data.planIds });
            // Delete the channel if plan creation fails
            await supabase.from('channels').delete().eq('id', channel.id);
            throw new BusinessError('Failed to create channel plan associations');
          }

          // Track plan assignments for new channel
          try {
            // Get version data for region_id
            const { data: version } = await supabase
              .from('provider_versions')
              .select('region_id')
              .eq('id', data.versionId)
              .single();

            if (version) {
              // Get plan details for tracking
              const { data: planDetails } = await supabase
                .from('plans')
                .select('id, name, color_index')
                .in('id', data.planIds);

              if (planDetails) {
                for (const planData of planDetails) {
                  await changeTrackingService.logPlanAssignment({
                    versionId: data.versionId,
                    providerId: data.providerId,
                    regionId: version.region_id,
                    channelId: channel.id,
                    actionType: 'CREATE',
                    newPlan: {
                      id: planData.id,
                      name: planData.name,
                      color: planData.color_index || 1
                    }
                  });
                }
              }
            }
          } catch (trackingError) {
            logger.error('Failed to track plan assignments:', { error: trackingError });
          }
        }

        // Create tag associations
        if (data.tagIds?.length) {
          const { error: tagsError } = await supabase
            .from('channel_tag_assignments')
            .insert(
              data.tagIds.map(tagId => ({
                channel_id: channel.id,
                tag_id: tagId
              }))
            );

          if (tagsError) {
            logger.error(tagsError, { channel_id: channel.id, tagIds: data.tagIds });
            // Note: Not deleting the channel if tag assignment fails, as tags are optional
            // Just log the error
          }
        }

        // Track channel creation
        try {
          // Get version data for region_id
          const { data: version } = await supabase
            .from('provider_versions')
            .select('region_id')
            .eq('id', data.versionId)
            .single();

          if (version) {
            await changeTrackingService.logEntityCreation({
              versionId: data.versionId,
              providerId: data.providerId,
              regionId: version.region_id,
              entityType: 'channels',
              entityId: channel.id,
              entityData: {
                id: channel.id,
                name: channel.name,
                description: channel.description,
                price: channel.price,
                category_id: channel.category_id,
                is_local: channel.is_local,
                is_duplicate: channel.is_duplicate
              }
            });
          }
        } catch (trackingError) {
          console.error('Failed to track channel creation:', trackingError);
        }

        // Fetch and return the complete channel data
        const { data: fullChannel, error: fetchError } = await supabase
          .from('channels')
          .select(`
            *,
            price,
            numbers:channel_numbers(
              id,
              number,
              is_sequence,
              type:channel_types(
                id,
                name,
                display_order
              )
            ),
            category:categories(
              id,
              name
            ),
            categories:channel_categories(
              id,
              is_default,
              category:categories(
                id,
                name,
                parent_id,
                display_order,
                price,
                description
              )
            ),
            plans:channel_plans(
              plan:plans(
                id,
                name,
                color_index
              )
            ),
            tags:channel_tag_assignments(
              tag_id,
              tag:channel_tags(
                id,
                name
              )
            )
          `)
          .eq('id', channel.id)
          .single();

        if (fetchError) {
          logger.error(fetchError, { id: channel.id });
          throw new BusinessError('Failed to fetch created channel');
        }

        return fullChannel as unknown as Channel;
      } catch (error) {
        logger.error(error, { data });
        throw error;
      }
    });
  },

  updateChannel: async (id: number, data: UpdateChannelData): Promise<Channel> => {
    return withTiming('updateChannel', async () => {
      try {
        if (!data.providerId || !data.versionId) {
          throw new ValidationError('Provider ID and Version ID are required');
        }

        // Get current channel data for change tracking
        const { data: currentChannel, error: currentChannelError } = await supabase
          .from('channels')
          .select('*')
          .eq('id', id)
          .single();

        if (currentChannelError) {
          logger.error(currentChannelError, { id, data });
          throw new BusinessError('Failed to fetch current channel data');
        }

        // Get region_id from version for change tracking
        const { data: version, error: versionError } = await supabase
          .from('provider_versions')
          .select('region_id')
          .eq('id', data.versionId)
          .single();

        if (versionError) {
          logger.error(versionError, { id, data });
          throw new BusinessError('Failed to fetch version data');
        }

        // Get provider's sequence setting
        const { data: provider, error: providerError } = await supabase
          .from('providers')
          .select('has_sequence_numbers')
          .eq('id', data.providerId)
          .single();

        if (providerError) {
          logger.error(providerError, { id, data });
          throw new BusinessError('Failed to fetch provider settings');
        }

        // Use centralized validation service for updates if name is being updated
        if (data.name) {
          const { channelValidationService } = await import('./validation');
          
          const validationContext = {
            providerId: data.providerId,
            versionId: data.versionId,
            excludeChannelId: id, // Exclude current channel from duplicate check
            is_local: data.is_local !== undefined ? data.is_local : currentChannel.is_local,
            is_duplicate: data.is_duplicate !== undefined ? data.is_duplicate : currentChannel.is_duplicate
          };

          const channelData = {
            name: data.name,
            numbers: data.numbers || []
          };

          const validation = await channelValidationService.validateChannel(channelData, validationContext);
          
          if (!validation.isValid) {
            throw new BusinessError(validation.errors[0] || 'Channel validation failed');
          }
        }

        // Update channel details
        const updateChannel = async () => {
          const { error: channelError } = await supabase
            .from('channels')
            .update(Object.fromEntries(
              Object.entries({
                name: data.name,
                description: data.description,
                category_id: data.categoryId,
                price: data.price,
                is_duplicate: data.is_duplicate
              }).filter(([, value]) => value !== undefined)
            ) as never)
            .eq('id', id)
            .select()
            .single();

          if (channelError) throw channelError;
        };

        try {
          await retryOperation('updateChannel', updateChannel, 3);

          // Track changes after successful update
          try {
            const newChannelData = {
              name: data.name || currentChannel.name,
              description: data.description !== undefined ? data.description : currentChannel.description,
              category_id: data.categoryId !== undefined ? data.categoryId : currentChannel.category_id,
              price: data.price !== undefined ? data.price : currentChannel.price,
            };

            const changes = changeTrackingService.generateFieldChanges(
              currentChannel,
              newChannelData,
              [...getTrackableFields('channels')]
            );

            if (changes.length > 0) {
              await changeTrackingService.logBulkChanges({
                versionId: data.versionId,
                providerId: data.providerId,
                regionId: version.region_id,
                entityType: 'channels',
                entityId: id,
                changes,
              });
            }
          } catch (changeTrackingError) {
            logger.error('Change tracking failed', { error: changeTrackingError, id, data });
            // Don't throw - change tracking failure shouldn't break the main operation
          }
        } catch (error) {
          logger.error(error, { id, data });
          throw new BusinessError('Failed to update channel', error);
        }

        // Handle category updates if categoryIds is defined (even if empty)
        if (data.categoryIds !== undefined) {
          logger.info('Handling category updates for channel', {
            channelId: id,
            categoryIds: data.categoryIds,
            categoryCount: data.categoryIds.length,
            defaultCategoryId: data.defaultCategoryId
          });

          // Get current category assignments for comparison
          const { data: currentCategories } = await supabase
            .from('channel_categories')
            .select(`
              category_id,
              categories:category_id (id, name)
            `)
            .eq('channel_id', id);

          const currentCategoryIds = (currentCategories || []).map(cc => cc.category_id);

          const updateCategories = async () => {
            // First delete existing category relationships
            const { error: deleteCategoryError } = await supabase
              .from('channel_categories')
              .delete()
              .eq('channel_id', id);

            if (deleteCategoryError) throw deleteCategoryError;

            // Only insert new relationships if there are categories to add
            if (data.categoryIds && data.categoryIds.length > 0) {
              // Determine the default category ID - use provided default or first category
              const defaultCategoryId = data.defaultCategoryId || data.categoryIds[0];

              // Then insert new category relationships
              const categoryRecords = data.categoryIds.map(categoryId => ({
                channel_id: id,
                category_id: categoryId,
                is_default: categoryId === defaultCategoryId
              }));
              
              logger.info('Inserting new category records', { categoryRecords });
              
              const { error: insertCategoryError } = await supabase
                .from('channel_categories')
                .insert(categoryRecords);

              if (insertCategoryError) throw insertCategoryError;
            } else {
              // If no categories selected, just set category_id to NULL
              const { error: updateCategoryError } = await supabase
                .from('channels')
                .update({ category_id: null })
                .eq('id', id);
                
              if (updateCategoryError) throw updateCategoryError;
              logger.info('Removed all categories from channel', { channelId: id });
            }
          };
          
          try {
            await retryOperation('updateChannelCategories', updateCategories, 3);

            // Track category assignment changes
            try {
              const newCategoryIds = data.categoryIds || [];

              // Track removed categories
              for (const currentCategoryId of currentCategoryIds) {
                if (!newCategoryIds.includes(currentCategoryId)) {
                  const currentCategory = (currentCategories || []).find(cc => cc.category_id === currentCategoryId);
                  if (currentCategory?.categories) {
                    await changeTrackingService.logCategoryAssignment({
                      versionId: data.versionId,
                      providerId: data.providerId,
                      regionId: version.region_id,
                      channelId: id,
                      actionType: 'DELETE',
                      oldCategory: {
                        id: currentCategoryId,
                        name: (currentCategory.categories as any)?.name
                      }
                    });
                  }
                }
              }

              // Track added categories
              if (newCategoryIds.length > 0) {
                const { data: newCategoryDetails } = await supabase
                  .from('categories')
                  .select('id, name')
                  .in('id', newCategoryIds);

                if (newCategoryDetails) {
                  for (const categoryData of newCategoryDetails) {
                    if (!currentCategoryIds.includes(categoryData.id)) {
                      await changeTrackingService.logCategoryAssignment({
                        versionId: data.versionId,
                        providerId: data.providerId,
                        regionId: version.region_id,
                        channelId: id,
                        actionType: 'CREATE',
                        newCategory: {
                          id: categoryData.id,
                          name: categoryData.name
                        }
                      });
                    }
                  }
                }
              }
            } catch (trackingError) {
              logger.error('Failed to track category assignment changes:', { error: trackingError });
            }
          } catch (error) {
            logger.error(error, { id, categoryIds: data.categoryIds });
            throw new BusinessError('Failed to update channel categories', error);
          }
        }

        // Handle number updates based on provider type
        if (provider?.has_sequence_numbers) {
          // For sequential providers, fix any sequence issues after updates
          await channelService.fixSequentialNumbers(data.providerId, data.versionId);
        } else if (data.numbers) {
          // For non-sequential providers, check for duplicate numbers before updating
          if (data.numbers.length > 0) {
            // Number validation is now handled by the centralized validation service above
            // (This validation runs for both name and number changes)
          }
          
          const updateNumbers = async () => {
            // Delete existing numbers
            const { error: deleteError } = await supabase
              .from('channel_numbers')
              .delete()
              .eq('channel_id', id);

            if (deleteError) throw deleteError;

            // Normalize and insert new numbers (convert empty strings to '*' for optional types)
            const { error: numbersError } = await supabase
              .from('channel_numbers')
              .insert(
                data.numbers?.map(n => ({
                  channel_id: id,
                  type_id: n.typeId,
                  number: !n.number || n.number.trim() === '' ? '*' : n.number,
                  is_sequence: false
                })) || []
              );

            if (numbersError) throw numbersError;
          };

          try {
            await retryOperation('updateChannelNumbers', updateNumbers, 3);
          } catch (error) {
            logger.error(error, { id, numbers: data.numbers });
            throw new BusinessError('Failed to update channel numbers', error);
          }
        }

        // Update plan assignments
        if (data.planIds) {
          // Get current plan assignments for comparison
          const { data: currentPlans } = await supabase
            .from('channel_plans')
            .select(`
              plan_id,
              plans:plan_id (id, name, color_index)
            `)
            .eq('channel_id', id);

          const currentPlanIds = (currentPlans || []).map(cp => cp.plan_id);

          const updatePlans = async () => {
            const { error: deleteError } = await supabase
              .from('channel_plans')
              .delete()
              .eq('channel_id', id)
              .eq('version_id', data.versionId); // CRITICAL FIX: Delete only for this version

            if (deleteError) throw deleteError;

            const { error: plansError } = await supabase
              .from('channel_plans')
              .insert(
                data.planIds?.map(planId => ({
                  channel_id: id,
                  plan_id: planId,
                  version_id: data.versionId
                })) || []
              );

            if (plansError) throw plansError;
          };

          try {
            await retryOperation('updateChannelPlans', updatePlans, 3);

            // Track plan assignment changes
            try {
              const newPlanIds = data.planIds || [];

              // Track removed plans
              for (const currentPlanId of currentPlanIds) {
                if (!newPlanIds.includes(currentPlanId)) {
                  const currentPlan = (currentPlans || []).find(cp => cp.plan_id === currentPlanId);
                  if (currentPlan?.plans) {
                    await changeTrackingService.logPlanAssignment({
                      versionId: data.versionId,
                      providerId: data.providerId,
                      regionId: version.region_id,
                      channelId: id,
                      actionType: 'DELETE',
                      oldPlan: {
                        id: currentPlanId,
                        name: (currentPlan.plans as any)?.name,
                        color: (currentPlan.plans as any)?.color_index || 1
                      }
                    });
                  }
                }
              }

              // Track added plans
              if (newPlanIds.length > 0) {
                const { data: newPlanDetails } = await supabase
                  .from('plans')
                  .select('id, name, color_index')
                  .in('id', newPlanIds);

                if (newPlanDetails) {
                  for (const planData of newPlanDetails) {
                    if (!currentPlanIds.includes(planData.id)) {
                      await changeTrackingService.logPlanAssignment({
                        versionId: data.versionId,
                        providerId: data.providerId,
                        regionId: version.region_id,
                        channelId: id,
                        actionType: 'CREATE',
                        newPlan: {
                          id: planData.id,
                          name: planData.name,
                          color: planData.color_index || 1
                        }
                      });
                    }
                  }
                }
              }
            } catch (trackingError) {
              logger.error('Failed to track plan assignment changes:', { error: trackingError });
            }
          } catch (error) {
            logger.error(error, { id, planIds: data.planIds });
            throw new BusinessError('Failed to update channel plans', error);
          }
        }

        // Update tag assignments
        if (data.tagIds !== undefined) {
          const updateTags = async () => {
            // Delete existing tag assignments
            const { error: deleteError } = await supabase
              .from('channel_tag_assignments')
              .delete()
              .eq('channel_id', id);

            if (deleteError) throw deleteError;

            // Insert new tag assignments
            if (data.tagIds && data.tagIds.length > 0) {
              const { error: tagsError } = await supabase
                .from('channel_tag_assignments')
                .insert(
                  data.tagIds.map(tagId => ({
                    channel_id: id,
                    tag_id: tagId
                  }))
                );

              if (tagsError) throw tagsError;
            }
          };

          try {
            await retryOperation('updateChannelTags', updateTags, 3);
          } catch (error) {
            logger.error(error, { id, tagIds: data.tagIds });
            // Don't throw for tag update errors, as tags are optional
            logger.warn('Failed to update channel tags, continuing with update', { id, tagIds: data.tagIds });
          }
        }

        // Fetch final channel with all relations
        const { data: fullChannel, error: fetchError } = await supabase
          .from('channels')
          .select(`
            *,
            price,
            category:categories!left(
              id,
              name
            ),
            numbers:channel_numbers(
              id,
              type_id,
              number,
              is_sequence,
              type:channel_types(
                id,
                name,
                display_order
              )
            ),
            categories:channel_categories(
              id,
              category_id,
              is_default,
              category:categories(
                id,
                name,
                parent_id,
                display_order,
                price,
                description
              )
            ),
            plans:channel_plans(
              plan:plans(
                id,
                name,
                color_index
              )
            ),
            tags:channel_tag_assignments(
              tag_id,
              tag:channel_tags(
                id,
                name
              )
            ),
            linked_provider:providers!linked_provider_id(
              id,
              name,
              logo_url,
              type_id
            )
          `)
          .eq('id', id)
          .single();

        if (fetchError) {
          logger.error(fetchError, { id });
          throw new BusinessError('Failed to fetch updated channel');
        }

        return fullChannel as unknown as Channel;
      } catch (error) {
        logger.error(error, { id, data });
        if (error instanceof BusinessError || error instanceof ValidationError) {
          throw error;
        }
        throw new BusinessError('Failed to update channel', error);
      }
    });
  },

  deleteChannel: async (id: number): Promise<void> => {
    return withTiming('deleteChannel', async () => {
      try {
        // Get channel data before deletion for change tracking
        const { data: channelToDelete, error: fetchError } = await supabase
          .from('channels')
          .select(`
            *,
            provider_versions!version_id(region_id)
          `)
          .eq('id', id)
          .single();

        if (fetchError) {
          logger.error(fetchError, { id });
          throw new BusinessError('Failed to fetch channel for deletion tracking', fetchError);
        }

        const deleteOperation = async () => {
          const { error } = await supabase
            .from('channels')
            .delete()
            .eq('id', id);

          if (error) throw error;
        };

        try {
          await retryOperation('deleteChannel', deleteOperation, 3);
        } catch (error) {
          logger.error(error, { id });
          throw new BusinessError('Failed to delete channel', error);
        }

        // Track channel deletion
        if (channelToDelete) {
          try {
            await changeTrackingService.logEntityDeletion({
              versionId: channelToDelete.version_id!,
              providerId: channelToDelete.provider_id!,
              regionId: (channelToDelete.provider_versions as any)[0]?.region_id,
              entityType: 'channels',
              entityId: id,
              entityData: {
                id: channelToDelete.id,
                name: channelToDelete.name,
                description: channelToDelete.description,
                price: channelToDelete.price,
                category_id: channelToDelete.category_id,
                is_local: channelToDelete.is_local,
                is_duplicate: channelToDelete.is_duplicate
              }
            });
          } catch (trackingError) {
            console.error('Failed to track channel deletion:', trackingError);
          }
        }
      } catch (error) {
        logger.error(error, { id });
        if (error instanceof BusinessError) {
          throw error;
        }
        throw new BusinessError('Failed to delete channel', error);
      }
    });
  },

  bulkUpdateChannels: async (updates: UpdateChannelInput[]): Promise<void> => {
    return withTiming('bulkUpdateChannels', async () => {
      try {
        // Make sure we have updates to process
        if (!updates.length) {
          return;
        }

        // Get provider settings to determine sequence handling
        const providerId = updates[0].providerId;
        const versionId = updates[0].versionId;
        
        const { data: provider, error: providerError } = await supabase
          .from('providers')
          .select('has_sequence_numbers')
          .eq('id', providerId)
          .single();

        if (providerError) {
          logger.error(providerError, { providerId, versionId });
          throw new BusinessError('Failed to fetch provider settings');
        }

        // Process updates in batches to avoid overwhelming the database
        const batchSize = 5;
        const batches = [];
        
        for (let i = 0; i < updates.length; i += batchSize) {
          batches.push(updates.slice(i, i + batchSize));
        }

        for (const batch of batches) {
          await Promise.all(
            batch.map(async (update) => {
              const { id, ...data } = update;
              try {
                await channelService.updateChannel(id, data as UpdateChannelData);
              } catch (error) {
                logger.error(error, { update });
                throw new BusinessError(
                  `Failed to update channel ${id}`,
                  error
                );
              }
            })
          );

          // Add a small delay between batches to prevent rate limiting
          if (batches.indexOf(batch) < batches.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }

        // Only assign sequence numbers for sequential providers
        if (provider?.has_sequence_numbers) {
          // After updates, fix any sequential number issues
          await channelService.fixSequentialNumbers(providerId, versionId);
        }
      } catch (error) {
        logger.error(error, { updates });
        throw error;
      }
    });
  },

  // Add a new method for bulk delete
  bulkDeleteChannels: async (ids: number[]): Promise<void> => {
    return withTiming('bulkDeleteChannels', async () => {
      try {
        // First get the channels to be deleted
        const { data: channels } = await supabase
          .from('channels')
          .select(`
            id,
            provider_id,
            version_id,
            numbers!inner(
              id,
              number,
              is_sequence
            )
          `)
          .in('id', ids)
          .order('numbers.number::integer');

        // Delete the channels
        const { error } = await supabase
          .from('channels')
          .delete()
          .in('id', ids);

        if (error) throw error;

        // Resequence remaining channels if needed
        if (channels?.[0] && channels[0].provider_id && channels[0].version_id) {
          await supabase.rpc('fix_duplicate_sequence_numbers', {
            p_provider_id: channels[0].provider_id,
            p_version_id: channels[0].version_id
          });
        }
      } catch (error) {
        logger.error(error, { ids });
        throw new BusinessError('Failed to delete channels', error);
      }
    });
  },

  fixSequentialNumbers: async (providerId: number, versionId: number) => {
    try {
      // First fix any duplicate sequence numbers
      const { error: duplicateError } = await supabase
        .rpc('fix_duplicate_sequence_numbers', {
          p_provider_id: providerId,
          p_version_id: versionId
        });

      if (duplicateError) {
        console.error('Fix duplicate sequence numbers error:', duplicateError);
        throw duplicateError;
      }

      // Then fix the sequence order
      const { error } = await supabase
        .rpc('fix_channel_sequence_numbers', {
          p_provider_id: providerId,
          p_version_id: versionId
        });

      if (error) {
        console.error('Fix sequential numbers error:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Fix sequential numbers error:', error);
      throw error;
    }
  },

  assignSequenceNumber: async (channelId: number, versionId: number) => {
    try {
      const { error } = await supabase
        .rpc('assign_channel_number', {
          p_channel_id: channelId,
          p_version_id: versionId
        });

      if (error) {
        console.error('Failed to assign sequence number:', error);
        throw new BusinessError('Failed to assign sequence number');
      }

      return true;
    } catch (error) {
      console.error('Assign sequence number error:', error);
      throw error;
    }
  },

  bulkAssignSequenceNumbers: async (
    providerId: number,
    versionId: number,
    channelIds: number[]
  ): Promise<void> => {
    try {
      const { error } = await supabase
        .rpc('assign_import_numbers', {
          p_provider_id: providerId,
          p_version_id: versionId,
          p_channel_ids: channelIds
        });

      if (error) {
        console.error('Failed to assign sequence numbers:', error);
        throw new BusinessError('Failed to assign sequence numbers');
      }
    } catch (error) {
      console.error('Bulk assign sequence numbers error:', error);
      throw error;
    }
  },

  /**
   * Copy channels between regions
   * @param sourceVersionId - Source version ID
   * @param targetVersionId - Target version ID
   * @param copyLocal - Whether to copy local channels (default: false)
   * @param specificChannelIds - Specific channel IDs to copy (optional)
   */
  copyChannelsBetweenRegions: async (
    sourceVersionId: number,
    targetVersionId: number,
    copyLocal: boolean = false,
    specificChannelIds?: number[]
  ) => {
    try {
      // Call the stored procedure
      const { data, error } = await supabase
        .rpc('copy_channels_between_regions', {
          p_source_version_id: sourceVersionId,
          p_target_version_id: targetVersionId,
          p_copy_local: copyLocal,
          p_specific_channel_ids: specificChannelIds || undefined
        });

      if (error) {
        logger.error(error, { sourceVersionId, targetVersionId, copyLocal, specificChannelIds });
        throw new BusinessError('Failed to copy channels between regions');
      }

      return data;
    } catch (error) {
      logger.error(error, { sourceVersionId, targetVersionId, copyLocal, specificChannelIds });
      throw error;
    }
  },
};

// Retry utility
const retryOperation = async (
  operation: string,
  fn: () => Promise<void>,
  maxRetries: number
): Promise<void> => {
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      await fn();
      return;
    } catch (error) {
      lastError = error as Error;
      logger.warn(`Retry attempt ${attempt + 1} for ${operation} failed`, { error });
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
    }
  }
  
  throw lastError;
};